import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CommandLineIcon,
  CircleStackIcon,
  ShieldCheckIcon,
  ServerStackIcon,
  LockClosedIcon,
  Squares2X2Icon,
  ArchiveBoxIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  ClockIcon,
  ClipboardDocumentCheckIcon,
  Cog6ToothIcon,
  UserGroupIcon,
  SignalIcon,
  ChartBarIcon,
  BoltIcon,
  WrenchScrewdriverIcon,
  BuildingOffice2Icon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import QueryConsoleTab from './database/QueryConsoleTab';
import TenantMigrationsTab from './database/TenantMigrationsTab';
import RegistryMigrationsTab from './database/RegistryMigrationsTab';
import SchemaTemplates from './SchemaTemplates';
import DataRetention from './DataRetention';
import StatusTab from './database/runner/StatusTab';
import HistoryTab from './database/runner/HistoryTab';
import CleanupTab from './database/runner/cleanup/CleanupTab';
import BackupsTab from './database/runner/BackupsTab';
import SettingsTab from './database/runner/SettingsTab';
import SafetyTab from './database/runner/SafetyTab';
import BatchView from './database/runner/BatchView';
import BatchDialog from './database/runner/BatchDialog';
import RunDetail from './database/runner/RunDetail';
import PreviewPanel from './database/runner/PreviewPanel';
import LiveTab from './database/ops/LiveTab';
import SizeTab from './database/ops/SizeTab';
import SlowTab from './database/ops/SlowTab';
import DataFixesTab from './database/ops/DataFixesTab';
import CompanyDataTab from './database/ops/CompanyDataTab';
import AlertsTab from './database/ops/AlertsTab';
import SchedulePanel from './database/ops/SchedulePanel';
import { Drawer, ErrorBox, Spinner, shortName } from './database/runner/ui';
import { CONSOLE_PREFILL_KEY, type BatchRequest, type RunnerNav } from './database/runner/nav';
import { runnerApi, errorText, type StatusGrid } from '../api/migrationRunner';

/**
 * The Database section (approved mockup "Database redesign", 2026-10-11).
 *
 * Status is the home: every company × every migration, with failed / partly
 * applied / interrupted runs called out. Runs, batches and previews open in a
 * side sheet from anywhere (their ids live in the URL: ?run=, ?batch=,
 * ?preview=), so a link to a failed run survives a reload. Migration files
 * keeps the per-file tools (saved scripts, ad-hoc SQL, archive, mark applied).
 * Backups, schema templates and retention are tabs here; their old routes
 * redirect in.
 */

type TabKey =
  | 'status'
  | 'files'
  | 'history'
  | 'cleanup'
  | 'backups'
  | 'console'
  | 'settings'
  | 'safety'
  | 'live'
  | 'size'
  | 'slow'
  | 'datafix'
  | 'companydata'
  | 'alerts'
  | 'schema'
  | 'retention';

interface TabDef {
  key: TabKey;
  label: string;
  ownerOnly: boolean;
  icon: React.ComponentType<{ className?: string }>;
}

const TABS: TabDef[] = [
  { key: 'status', label: 'Status', ownerOnly: false, icon: Squares2X2Icon },
  { key: 'cleanup', label: 'Clean up', ownerOnly: false, icon: ClipboardDocumentCheckIcon },
  { key: 'files', label: 'Migration files', ownerOnly: true, icon: CircleStackIcon },
  { key: 'history', label: 'History', ownerOnly: false, icon: ClockIcon },
  { key: 'backups', label: 'Backups', ownerOnly: false, icon: ArchiveBoxIcon },
  { key: 'console', label: 'Query console', ownerOnly: false, icon: CommandLineIcon },
  { key: 'settings', label: 'Settings', ownerOnly: false, icon: Cog6ToothIcon },
  { key: 'safety', label: 'Safety', ownerOnly: false, icon: UserGroupIcon },
  { key: 'live', label: 'Live activity', ownerOnly: false, icon: SignalIcon },
  { key: 'size', label: 'Size and growth', ownerOnly: false, icon: ChartBarIcon },
  { key: 'slow', label: 'Slow queries', ownerOnly: false, icon: BoltIcon },
  { key: 'datafix', label: 'Data fixes', ownerOnly: false, icon: WrenchScrewdriverIcon },
  { key: 'companydata', label: 'Company data', ownerOnly: false, icon: BuildingOffice2Icon },
  { key: 'alerts', label: 'Alerts', ownerOnly: false, icon: BellAlertIcon },
  { key: 'schema', label: 'Templates', ownerOnly: true, icon: DocumentDuplicateIcon },
  { key: 'retention', label: 'Retention', ownerOnly: true, icon: TrashIcon },
];

/** Old ?tab= values → their new home. */
const LEGACY_TABS: Record<string, { tab: TabKey; files?: 'tenant' | 'registry' }> = {
  overview: { tab: 'status' },
  migrations: { tab: 'files', files: 'tenant' },
  registry: { tab: 'files', files: 'registry' },
  // Schema health moved into Clean up (Template › By company; folder lint on its overview).
  health: { tab: 'cleanup' },
};

const DatabaseTools: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [newCountTenant, setNewCountTenant] = useState(0);
  const [newCountRegistry, setNewCountRegistry] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [batchReq, setBatchReq] = useState<BatchRequest | null>(null);
  const [previewGrid, setPreviewGrid] = useState<StatusGrid | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [approvalsWaiting, setApprovalsWaiting] = useState(0);
  const [cleanupBadge, setCleanupBadge] = useState(0);
  const handleTenantNewCount = useCallback((count: number) => setNewCountTenant(count), []);
  const handleRegistryNewCount = useCallback((count: number) => setNewCountRegistry(count), []);

  const hasDbAccess = user?.can_access_database_tools ?? false;
  const isOwner = user?.role === 'owner';

  const visibleTabs = useMemo(() => TABS.filter((t) => !t.ownerOnly || isOwner), [isOwner]);

  const rawTab = searchParams.get('tab');
  const legacy = rawTab ? LEGACY_TABS[rawTab] : undefined;
  const requested = (legacy?.tab ?? rawTab) as TabKey | null;
  const activeTab = visibleTabs.find((t) => t.key === requested) ?? visibleTabs[0];
  const filesScope: 'tenant' | 'registry' = (searchParams.get('files') as 'tenant' | 'registry') || legacy?.files || 'tenant';

  const runId = Number(searchParams.get('run')) || null;
  const batchId = searchParams.get('batch');
  const previewId = Number(searchParams.get('preview')) || null;

  const setParams = useCallback((mut: (p: URLSearchParams) => void) => {
    const next = new URLSearchParams(searchParams);
    mut(next);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const selectTab = useCallback((key: TabKey) => setParams((p) => { p.set('tab', key); p.delete('files'); }), [setParams]);
  const closeSheet = useCallback(() => setParams((p) => { p.delete('run'); p.delete('batch'); p.delete('preview'); }), [setParams]);
  const bump = useCallback(() => setRefreshKey((k) => k + 1), []);

  const nav: RunnerNav = useMemo(() => ({
    openRun: (rowId) => setParams((p) => { p.delete('batch'); p.delete('preview'); p.set('run', String(rowId)); }),
    openBatch: (id) => setParams((p) => { p.delete('run'); p.delete('preview'); p.set('batch', id); }),
    openPreview: (scriptId) => setParams((p) => { p.delete('run'); p.delete('batch'); p.set('preview', String(scriptId)); }),
    openBatchDialog: (req) => setBatchReq(req),
    openConsole: (target, sql) => {
      try { sessionStorage.setItem(CONSOLE_PREFILL_KEY, JSON.stringify({ target, sql })); } catch { /* ignore */ }
      setParams((p) => { p.delete('run'); p.delete('batch'); p.delete('preview'); p.set('tab', 'console'); });
    },
  }), [setParams]);

  // Requests waiting for this owner's decision (Safety tab badge; there is no bell).
  useEffect(() => {
    if (!hasDbAccess) return;
    const check = () => runnerApi.approvals('pending')
      .then(({ approvals, me }) => setApprovalsWaiting(approvals.filter((a) => a.requestedBy.toLowerCase() !== me.toLowerCase()).length))
      .catch(() => { /* not set up yet */ });
    check();
    const t = setInterval(check, 60000);
    return () => clearInterval(t);
  }, [hasDbAccess]);

  // Clean up badge (pending pairs + stuck runs); the tab refreshes it while open.
  useEffect(() => {
    if (!hasDbAccess) return;
    const check = () => runnerApi.cleanupOverview().then((o) => setCleanupBadge(o.counts.badge)).catch(() => { /* runner not set up */ });
    check();
    const t = setInterval(check, 300000);
    return () => clearInterval(t);
  }, [hasDbAccess, refreshKey]);

  // The preview needs the migration and its databases; read them from the grid.
  useEffect(() => {
    if (!previewId) return;
    setPreviewError(null);
    runnerApi.status(true).then(setPreviewGrid).catch((err) => setPreviewError(errorText(err, 'Could not load the migration')));
  }, [previewId, refreshKey]);

  const previewMigration = previewId ? previewGrid?.migrations.find((m) => m.scriptId === previewId) ?? null : null;
  const previewTargets = useMemo(() => {
    if (!previewGrid || !previewMigration) return [];
    return previewGrid.targets
      .filter((t) => t.scope === previewMigration.scope)
      .map((t) => ({ ...t, pending: previewGrid.cells[previewMigration.scriptId]?.[t.targetDb]?.state === 'not_run' }));
  }, [previewGrid, previewMigration]);

  if (!hasDbAccess) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-slate-50 to-white">
        <div className="text-center">
          <LockClosedIcon className="mx-auto mb-4 h-16 w-16 text-slate-300" />
          <h2 className="mb-2 text-xl font-bold text-slate-900">Access Denied</h2>
          <p className="max-w-sm text-sm text-slate-500">You do not have permission to access database tools.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/80 backdrop-blur-md">
        <div className="mx-auto max-w-7xl px-6 py-5">
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            <CircleStackIcon className="h-7 w-7 text-blue-600" />
            Database
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Migrations on every company, clean up, backups and restore, and the query console
          </p>

          <div className="-mb-px mt-4 flex gap-1 overflow-x-auto border-b border-slate-200" role="tablist">
            {visibleTabs.map((t) => {
              const Icon = t.icon;
              const active = t.key === activeTab.key;
              const newCount = t.key === 'files' ? newCountTenant + newCountRegistry : 0;
              return (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => selectTab(t.key)}
                  className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                    active ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {t.label}
                  {t.key === 'cleanup' && cleanupBadge > 0 && (
                    <span className="ml-1.5 rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-800">
                      {cleanupBadge.toLocaleString()}
                    </span>
                  )}
                  {t.key === 'safety' && approvalsWaiting > 0 && (
                    <span className="ml-1.5 rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-800">
                      {approvalsWaiting} to approve
                    </span>
                  )}
                  {newCount > 0 && (
                    <span className="ml-1.5 rounded-md border border-red-200 bg-red-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-red-700">
                      {newCount} new
                    </span>
                  )}
                </button>
              );
            })}
            {!isOwner && (
              <span className="ml-auto inline-flex items-center gap-1 self-center whitespace-nowrap text-xs text-slate-400">
                <ShieldCheckIcon className="h-3.5 w-3.5" />
                Some tabs and actions require the owner role
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl">
        {activeTab.key === 'status' && <StatusTab nav={nav} isOwner={isOwner} refreshKey={refreshKey} />}
        {activeTab.key === 'files' && (
          <div>
            <div className="flex gap-2 px-6 pt-5">
              {(['tenant', 'registry'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setParams((p) => { p.set('tab', 'files'); p.set('files', s); })}
                  className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[13px] font-bold ${
                    filesScope === s ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {s === 'tenant' ? <CircleStackIcon className="h-4 w-4" /> : <ServerStackIcon className="h-4 w-4" />}
                  {s === 'tenant' ? 'Company migrations' : 'Registry migrations'}
                  {(s === 'tenant' ? newCountTenant : newCountRegistry) > 0 && (
                    <span className="rounded bg-red-100 px-1.5 text-[10px] text-red-700">{s === 'tenant' ? newCountTenant : newCountRegistry} new</span>
                  )}
                </button>
              ))}
            </div>
            {filesScope === 'tenant'
              ? <TenantMigrationsTab onNewCountChange={handleTenantNewCount} />
              : <RegistryMigrationsTab onNewCountChange={handleRegistryNewCount} />}
          </div>
        )}
        {activeTab.key === 'history' && <HistoryTab nav={nav} refreshKey={refreshKey} />}
        {activeTab.key === 'cleanup' && <CleanupTab nav={nav} isOwner={isOwner} refreshKey={refreshKey} onBadge={setCleanupBadge} />}
        {activeTab.key === 'backups' && <BackupsTab isOwner={isOwner} />}
        {activeTab.key === 'console' && <QueryConsoleTab isOwner={isOwner} />}
        {activeTab.key === 'settings' && (
          <div className="space-y-4">
            <SettingsTab isOwner={isOwner} />
            <SchedulePanel isOwner={isOwner} />
          </div>
        )}
        {activeTab.key === 'live' && <LiveTab isOwner={isOwner} />}
        {activeTab.key === 'size' && <SizeTab isOwner={isOwner} />}
        {activeTab.key === 'slow' && <SlowTab />}
        {activeTab.key === 'datafix' && <DataFixesTab isOwner={isOwner} />}
        {activeTab.key === 'companydata' && <CompanyDataTab isOwner={isOwner} />}
        {activeTab.key === 'alerts' && <AlertsTab isOwner={isOwner} />}
        {activeTab.key === 'safety' && <SafetyTab isOwner={isOwner} onPendingCount={setApprovalsWaiting} />}
        {activeTab.key === 'schema' && (
          <div className="px-6 py-6">
            <SchemaTemplates embedded />
          </div>
        )}
        {activeTab.key === 'retention' && (
          <div className="px-6 py-6">
            <DataRetention embedded />
          </div>
        )}
      </div>

      <Drawer open={!!runId} onClose={closeSheet} title="Migration run" wide>
        {runId && <RunDetail key={runId} rowId={runId} onOpenBatch={nav.openBatch} onOpenConsole={nav.openConsole} onChanged={bump} />}
      </Drawer>
      <Drawer open={!!batchId} onClose={closeSheet} title="Batch">
        {batchId && <BatchView key={batchId} batchId={batchId} onOpenRun={nav.openRun} onOpenBatch={nav.openBatch} onChanged={bump} />}
      </Drawer>
      <Drawer open={!!previewId} onClose={closeSheet} title={previewMigration ? `Preview · ${shortName(previewMigration.folderPath)}` : 'Preview'} wide>
        {previewError && <ErrorBox>{previewError}</ErrorBox>}
        {!previewError && !previewMigration && <div className="flex items-center gap-2 text-sm text-slate-500"><Spinner /> Loading…</div>}
        {previewMigration && (
          <PreviewPanel
            key={previewMigration.scriptId}
            migration={previewMigration}
            targets={previewTargets}
            onRun={(targets) => setBatchReq({
              migrationIds: [previewMigration.scriptId],
              targets,
              title: `Apply ${shortName(previewMigration.folderPath)} to ${targets.length} database${targets.length === 1 ? '' : 's'}`,
            })}
          />
        )}
      </Drawer>

      {batchReq && (
        <BatchDialog
          open
          onClose={() => setBatchReq(null)}
          migrationIds={batchReq.migrationIds}
          targets={batchReq.targets}
          onlyPairs={batchReq.onlyPairs}
          title={batchReq.title}
          onStarted={(b) => { setBatchReq(null); bump(); nav.openBatch(b.id); }}
        />
      )}
    </div>
  );
};

export default DatabaseTools;
